import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {addQueryToPath} from '../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {ApiLenses} from '../../../../models/api/lensModel';
import {VisualWorkspaceLens} from '../../../../models/api/visualWorkspaceModel';
import {postApiAsync} from '../../../../services/http/http';
import {HttpError} from '../../../../services/http/httpError';
import {Dispatch} from '../../../../store/appStore';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {updateDataAction} from '../../../../store/data/dataGroupsActions';
import {WorkspaceDropReorderLensHandler} from '../../../../store/data/workspace/reorder/types';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {Skeleton} from '../../../common/skeleton';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {CreateLensButton} from '../lensesWorkspace/createLensButton';
import {LensesWorkspaceCreateDialog} from '../lensesWorkspace/dialog/lensesWorkspaceCreateDialog';
import {Workspace} from '../workspace';
import {LensUpdateHandler, ToggleLensHandler} from './context/workspacesEditorContext';
import {WorkspacesEditorLensesContainer} from './workspacesEditorLensesContainer';

/*
 * Props.
 */

interface LensesWorkspaceEditorProps {
  panelId: InboxesPanelId;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpand: Handler;
  /** The list of lenses displayed in the workspace. */
  visualWorkspaceLenses: ReadonlyArray<VisualWorkspaceLens>;
  /** Whether workspace is alphabetized. */
  isAlphabetized: boolean;
  /** Event handler for drag and drop re-ordering */
  onDropReorderLens: WorkspaceDropReorderLensHandler;
  /** Event handler to select/remove lenses in P1. */
  onToggleLens: ToggleLensHandler;
  /** Event handler to select/remove lenses in P1. */
  onCreateLens: LensUpdateHandler;
  isCreateDialogOpen: boolean;
  showCreateLensDialog: Handler;
  hideCreateLensDialog: Handler;

  hasTeamLenses: boolean;
}

/*
 * Styles.
 */

const StyledSkeleton = styled(Skeleton)`
  height: 30px;
  width: 90%;
  margin: 3px 5%;
`;

/*
 * Component.
 */

export const LensesWorkspaceEditor: FC<LensesWorkspaceEditorProps> = (props) => {
  const {
    panelId,
    isExpanded,
    onToggleExpand,
    visualWorkspaceLenses,
    onCreateLens,
    isCreateDialogOpen,
    showCreateLensDialog,
    hideCreateLensDialog,
  } = props;
  const companyUrl = useAppSelector(getCompanyUrl);
  const dispatch = useAppDispatch();

  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);

  useInitialEffect(() => {
    fetchHiddenLenses(visualWorkspaceLenses, setLoadingStatus, companyUrl, dispatch);
  });

  return (
    <Workspace isExpanded={isExpanded}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        interactionId="lenses"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpand}
        isEditingWorkspace
      >
        <Trans id="workspace-lenses-title">Views</Trans>
      </WorkspaceHeader>

      {/* Workspace content. */}
      {isExpanded && renderLensesList(loadingStatus, props, showCreateLensDialog)}

      {/* Create view Dialog. */}
      {isCreateDialogOpen && (
        <LensesWorkspaceCreateDialog
          panelId={panelId}
          onRequestClose={hideCreateLensDialog}
          workspaceUpdateHandler={onCreateLens}
        />
      )}
    </Workspace>
  );
};

/*
 * Helpers.
 */

const fetchHiddenLenses = async (
  visualWorkspaceLenses: ReadonlyArray<VisualWorkspaceLens>,
  setLoadingStatus: HandlerOf<AsyncStatusesEnum>,
  companyUrl: string,
  dispatch: Dispatch,
) => {
  const hiddenLensIds = visualWorkspaceLenses.filter(({isSelected}) => !isSelected).map(({id}) => id);

  if (hiddenLensIds.length < 1) {
    setLoadingStatus(AsyncStatusesEnum.LOADED);
    return;
  }

  const url = addQueryToPath(`${companyUrl}/lenses/fetch_batch`);
  const requestBody = {
    ids: hiddenLensIds,
  };

  try {
    const lenses = await postApiAsync(ApiLenses, url, requestBody);
    dispatch(updateDataAction({lenses}));
    setLoadingStatus(AsyncStatusesEnum.LOADED);
  } catch (error) {
    if (error instanceof HttpError) {
      return;
    }

    reportToBugsnag(error); // Report non-HTTP errors to Bugsnag.
    setLoadingStatus(AsyncStatusesEnum.FAILED);
  }
};

function renderLensesList(
  loadingStatus: AsyncStatusesEnum,
  props: LensesWorkspaceEditorProps,
  onCreateClick: Handler,
) {
  const {panelId, isAlphabetized, visualWorkspaceLenses, onDropReorderLens, onToggleLens} = props;

  if (loadingStatus !== AsyncStatusesEnum.LOADED) {
    return (
      <>
        <StyledSkeleton />
        <StyledSkeleton />
      </>
    );
  }

  return (
    <>
      <WorkspacesEditorLensesContainer
        panelId={panelId}
        isAlphabetized={isAlphabetized}
        visualWorkspaceLenses={visualWorkspaceLenses}
        onDropReorderLens={onDropReorderLens}
        onToggleLens={onToggleLens}
      />
      <CreateLensButton onClick={onCreateClick} />
    </>
  );
}
